"use client";
import React from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import styles from "./workExperience.module.css";

export interface ProfessionalExperienceItem {
  title: string;
  dateRange: string;
  description: string;
}

export interface EducationItem {
  title: string;
  description: string;
}

export interface CertificationItem {
  title: string;
  dateRange: string;
  description: string;
}

export interface WorkExperienceProps {
  professionalExperience?: ProfessionalExperienceItem[];
  education?: EducationItem[];
  certifications?: CertificationItem[];
}

const WorkExperience: React.FC<WorkExperienceProps> = ({
  professionalExperience = [],
  education = [],
  certifications = [],
}) => {
  const [sectionRef, isVisible] = useScrollAnimation();
  const [hasAnimated, setHasAnimated] = React.useState(false);

  React.useEffect(() => {
    if (isVisible && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isVisible, hasAnimated]);

  const hasProfessionalExperience = professionalExperience.length > 0;
  const hasEducation = education.length > 0;
  const hasCertifications = certifications.length > 0;

  // Don't render if no data
  if (!hasProfessionalExperience && !hasEducation && !hasCertifications) {
    return null;
  }

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      className={`w-full py-20 px-6 lg:px-8 ${hasAnimated ? styles.sectionAnimation : ""}`}
      aria-label="Work Experience"
    >
      <div className="mx-auto max-w-4xl flex flex-col gap-12">
        {hasProfessionalExperience && (
          <article className="w-full">
            <div className={`flex items-center gap-3 mb-10 ${hasAnimated ? styles.headerAnimation : ""}`}>
              <svg
                className="w-6 h-6 text-white flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <h2 className="font-assistant font-semibold text-white text-base tracking-[0] leading-6">
                Professional Experience
              </h2>
            </div>

            <div className="flex flex-col gap-[30px] ml-3 pl-4 border-l-2 border-[#a78bfa]">
              {professionalExperience.map((experience, index) => (
                <div
                  key={index}
                  className={`flex flex-col sm:flex-row sm:items-start gap-4 px-5 ${hasAnimated ? styles.itemAnimation : ""}`}
                  style={hasAnimated ? { animationDelay: `${0.2 + index * 0.1}s` } : undefined}
                >
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <h3 className="font-assistant font-semibold text-white text-2xl tracking-[0] leading-6 flex-1">
                        {experience.title}
                      </h3>
                      <time className="font-inter font-normal text-slate-300 text-sm tracking-[0] leading-6 sm:text-right whitespace-nowrap">
                        {experience.dateRange}
                      </time>
                    </div>
                    <p className="font-inter font-normal text-slate-300 text-base tracking-[0] leading-6">
                      {experience.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </article>
        )}

        {hasEducation && (
          <article className="w-full">
            <div className={`flex items-center gap-3 mb-10 ${hasAnimated ? styles.headerAnimation : ""}`}>
              <svg
                className="w-6 h-6 text-white flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 14l9-5-9-5-9 5 9 5z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 14v9M12 14l-9-5M12 14l9-5M12 5v9"
                />
              </svg>
              <h2 className="font-assistant font-semibold text-white text-base tracking-[0] leading-6">
                Education
              </h2>
            </div>

            <div className="flex flex-col gap-[30px] ml-3 pl-4 border-l-2 border-[#a78bfa]">
              {education.map((edu, index) => (
                <div
                  key={index}
                  className={`flex flex-col gap-4 px-5 ${hasAnimated ? styles.itemAnimation : ""}`}
                  style={hasAnimated ? { animationDelay: `${0.2 + index * 0.1}s` } : undefined}
                >
                  <h3 className="font-assistant font-semibold text-white text-2xl tracking-[0] leading-6">
                    {edu.title}
                  </h3>
                  <p className="font-inter font-normal text-slate-300 text-base tracking-[0] leading-6">
                    {edu.description}
                  </p>
                </div>
              ))}
            </div>
          </article>
        )}

        {hasCertifications && (
          <article className="w-full">
            <div className={`flex items-center gap-3 mb-10 ${hasAnimated ? styles.headerAnimation : ""}`}>
              <svg
                className="w-6 h-6 text-white flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h2 className="font-assistant font-semibold text-white text-base tracking-[0] leading-6">
                Certifications
              </h2>
            </div>

            <div className="flex flex-col gap-[30px] ml-3 pl-4 border-l-2 border-[#a78bfa]">
              {certifications.map((certification, index) => (
                <div
                  key={index}
                  className={`flex flex-col sm:flex-row sm:items-start gap-4 px-5 ${hasAnimated ? styles.itemAnimation : ""}`}
                  style={hasAnimated ? { animationDelay: `${0.2 + index * 0.1}s` } : undefined}
                >
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <h3 className="font-assistant font-semibold text-white text-2xl tracking-[0] leading-6 flex-1">
                        {certification.title}
                      </h3>
                      <time className="font-inter font-normal text-slate-300 text-sm tracking-[0] leading-6 sm:text-right whitespace-nowrap">
                        {certification.dateRange}
                      </time>
                    </div>
                    <p className="font-inter font-normal text-slate-300 text-base tracking-[0] leading-6">
                      {certification.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </article>
        )}
      </div>
    </section>
  );
};

export default WorkExperience;

